import { ComponentPropsWithoutRef } from 'react';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';
// Types
import type {
    AlbumCardGridLayoutType,
    AlbumCardMedia,
    AlbumCriteria,
    AlbumTypes
} from '@/lib/albums/types';

//libs
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import MediaGridFacebook from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridFacebook';
import MediaGridInstagram from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridInstagram';
import MediaGridLinkedin from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridLinkedin';
import MediaGridPinterest from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridPinterest';
import MediaGridTiktok from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridTiktok';
import MediaGridTwitter from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridTwitter';
import MediaGridYoutube from '@/components/pages/albums/AlbumCardMediaGrid/MediaGridYoutube';

const AlbumType = ({ type, hasBackdrop }: { type: AlbumTypes; hasBackdrop: boolean }) => {
    const { t } = useTranslation('content');

    return (
        <div
            className={clsx(
                'absolute right-0 top-0 z-10 m-2 flex h-6 w-12 items-center justify-center rounded bg-icoBlack/20 text-xs text-white',
                { 'backdrop-blur-xl': hasBackdrop }
            )}>
            {t(`type_album.${type}`)}
        </div>
    );
};

// distinguish between posts and stories for IG and Groups
const AlbumPostType = ({ criteria }: { criteria: AlbumCriteria }) => {
    const { t } = useTranslation('content');

    // Check if it's stories: album_type for SP, or post_types containing only 'story' for groups
    const isStories =
        criteria?.album_type === 'stories' ||
        (criteria?.post_types?.length === 1 && criteria?.post_types?.[0] === 'story');

    const currentType = isStories ? 'stories' : 'posts';

    return (
        <div
            className={clsx(
                'absolute left-0 top-0 z-10 m-2 flex h-6 items-center justify-center space-x-1 rounded px-2 text-xs',
                {
                    'bg-icoPurple-100 text-icoPurple': currentType === 'stories',
                    'bg-icoBlue-100 text-icoBlue': currentType === 'posts'
                }
            )}>
            <Icon icon={currentType === 'stories' ? 'story' : 'content'} />
            <span>{t(`post_type_album.${currentType}`)}</span>
        </div>
    );
};

type AlbumCardMediaGridProps = {
    medias: AlbumCardMedia[];
    type?: AlbumTypes;
    forcedNetwork?: SocialProfileNetworkType;
    criteria: AlbumCriteria;
    gridLayout?: AlbumCardGridLayoutType;
    isGroup?: boolean;
};

function MediaGrid({
    network,
    medias,
    criteria,
    gridLayout = 'card'
}: {
    network: SocialProfileNetworkType;
    medias: AlbumCardMedia[];
    criteria: AlbumCriteria;
    gridLayout?: AlbumCardGridLayoutType;
}) {
    switch (network) {
        case YOUTUBE:
            return <MediaGridYoutube medias={medias} gridLayout={gridLayout} />;
        case TWITTER:
            return <MediaGridTwitter medias={medias} gridLayout={gridLayout} />;
        case LINKEDIN:
            return <MediaGridLinkedin medias={medias} gridLayout={gridLayout} />;
        case PINTEREST:
            return <MediaGridPinterest medias={medias} gridLayout={gridLayout} />;
        case TIKTOK_BUSINESS:
            return <MediaGridTiktok medias={medias} gridLayout={gridLayout} />;
        case FACEBOOK:
            return <MediaGridFacebook medias={medias} gridLayout={gridLayout} />;
        case INSTAGRAM:
        default:
            if (criteria?.album_type === 'stories') {
                return <MediaGridTiktok medias={medias} gridLayout={gridLayout} />;
            }
            return <MediaGridInstagram medias={medias} gridLayout={gridLayout} />;
    }
}

type AlbumMediaGridWrapperProps = ComponentPropsWithoutRef<'div'> &
    Pick<AlbumCardMediaGridProps, 'criteria' | 'medias'> & {
        network: SocialProfileNetworkType;
        gridLayout?: AlbumCardGridLayoutType;
    };

function AlbumMediaGridWrapper({
    network,
    criteria,
    medias,
    gridLayout = 'card',
    ...restProps
}: AlbumMediaGridWrapperProps) {
    const isSingleStoryFormat =
        (network === TIKTOK_BUSINESS || criteria?.album_type === 'stories') && medias?.length === 1;

    return (
        <div
            className={clsx('grid h-full w-full grid-rows-12 overflow-hidden', {
                'grid-cols-9': isSingleStoryFormat,
                'bg-icoGray-300 dark:bg-icoDarkMode-anthracite':
                    isSingleStoryFormat && gridLayout === 'card',
                'grid-cols-12': !isSingleStoryFormat,
                'gap-1 rounded-md': gridLayout === 'card',
                'gap-0.5': gridLayout === 'table'
            })}
            {...restProps}
        />
    );
}

const AlbumCardMediaGrid = ({
    medias,
    type,
    criteria,
    forcedNetwork,
    gridLayout = 'card',
    isGroup = false
}: AlbumCardMediaGridProps) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = forcedNetwork ?? activeSocialProfile?.social_network_type;
    const shouldShowPostType = (network === INSTAGRAM || isGroup) && gridLayout === 'card';

    if (!medias.length) {
        return (
            <div
                className={clsx(
                    'albumImgGrid flex h-full w-auto justify-center overflow-hidden dark:bg-icoDarkMode-deepBlack',
                    {
                        'rounded-lg bg-icoGray-300': gridLayout === 'card'
                    }
                )}>
                <div className="flex w-full items-center justify-center overflow-hidden rounded-md">
                    {shouldShowPostType ? <AlbumPostType criteria={criteria} /> : null}
                    {gridLayout === 'card' ? <AlbumType type={type} hasBackdrop={false} /> : null}
                    <Icon
                        data-testid="album-icon-id"
                        icon="posts"
                        className={clsx('m-auto text-icoGray-500 dark:text-icoDarkMode-wolf', {
                            'text-6xl': gridLayout === 'card',
                            'text-xs': gridLayout === 'table'
                        })}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="albumImgGrid h-full">
            <AlbumMediaGridWrapper
                network={network}
                medias={medias}
                criteria={criteria}
                gridLayout={gridLayout}>
                {shouldShowPostType ? <AlbumPostType criteria={criteria} /> : null}
                {gridLayout === 'card' ? (
                    <AlbumType
                        type={type}
                        hasBackdrop={network !== LINKEDIN && network !== TWITTER}
                    />
                ) : null}
                <MediaGrid
                    network={network}
                    medias={medias}
                    criteria={criteria}
                    gridLayout={gridLayout}
                />
            </AlbumMediaGridWrapper>
        </div>
    );
};

export default AlbumCardMediaGrid;
